import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Image, ListChecks, DollarSign, Send, Download, Sparkles, Wand2, MessageSquare, Target } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import { AIPanel, AIButton, AIExplanationBox, AIBadge } from '../components/AIComponents';

const mockProposal = {
  id: 2,
  title: 'Shop Fit-out Package',
  client: { name: 'Retail Group', email: 'projects@retailgroup.com', phone: '0800 123 456' },
  status: 'sent',
  date: '2024-01-14',
  coverImage: null,
  introduction: 'Thank you for the opportunity to present our proposal for your new retail space fit-out. We understand your vision for a modern, customer-focused environment and are excited to bring it to life.',
  scope: [
    {
      title: 'Phase 1: Demolition & Preparation',
      description: 'Complete removal of existing fixtures, preparation of space including levelling floors and treating walls.',
      items: ['Remove existing shop fittings', 'Strip and prepare walls', 'Level and prepare flooring', 'Initial electrical assessment']
    },
    {
      title: 'Phase 2: Core Build',
      description: 'Installation of core structural elements, partitions, and building services.',
      items: ['Install new partition walls', 'Fit suspended ceiling grid', 'Install new flooring', 'Run electrical and data cabling']
    },
    {
      title: 'Phase 3: Fit-out & Finishing',
      description: 'Installation of retail fixtures, lighting, and finishing touches.',
      items: ['Install display units', 'Fit LED lighting system', 'Install point of sale area', 'Final decoration and signage']
    }
  ],
  pricing: {
    labour: 18000,
    materials: 15000,
    fixtures: 6000,
    contingency: 3000,
    subtotal: 42000,
    vat: 8400,
    total: 50400
  },
  timeline: '6-8 weeks from commencement',
  warranty: '12 months on all workmanship'
};

export function ProposalDetail() {
  const { id } = useParams();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 'var(--space-xl)', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Main Content */}
      <div className="detail-page" style={{ maxWidth: 'none' }}>
        <Link to="/quotes/proposals" className="back-link"><ArrowLeft size={16} />Back to Proposals</Link>

        <div className="detail-header">
          <div className="detail-title-row">
            <div>
              <h1 className="detail-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                {mockProposal.title}
                <AIBadge text="AI Enhanced" />
              </h1>
              <p className="detail-subtitle">Proposal for {mockProposal.client.name}</p>
            </div>
            <span className={`status-badge ${mockProposal.status === 'accepted' ? 'completed' : mockProposal.status === 'viewed' ? 'active' : mockProposal.status === 'sent' ? 'pending' : 'draft'}`} style={{ padding: '8px 16px', fontSize: '0.75rem' }}>{mockProposal.status}</span>
          </div>
        </div>

        {/* Proposal Preview */}
        <div className="proposal-preview mb-xl">
          {/* Cover Image Placeholder */}
          <div className="proposal-cover">
            <div className="proposal-cover-placeholder">
              <Image size={48} style={{ marginBottom: 'var(--space-md)' }} />
              <p>Cover Image Placeholder</p>
              <p style={{ fontSize: '0.75rem', marginTop: 'var(--space-xs)' }}>Drag and drop or click to upload</p>
            </div>
          </div>

          <div className="proposal-content">
            {/* Introduction */}
            <div className="proposal-section">
              <h3 className="proposal-section-title">Introduction</h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>{mockProposal.introduction}</p>
            </div>

            {/* Scope of Work */}
            <div className="proposal-section">
              <h3 className="proposal-section-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <ListChecks size={18} style={{ color: 'var(--accent-primary)' }} />
                Scope Summary
              </h3>
              {mockProposal.scope.map((phase, index) => (
                <div key={index} className="scope-item" style={{ marginBottom: 'var(--space-md)' }}>
                  <h4 className="scope-item-title">{phase.title}</h4>
                  <p className="scope-item-description">{phase.description}</p>
                  <ul style={{ marginTop: 'var(--space-sm)', paddingLeft: 'var(--space-lg)', color: 'var(--text-muted)' }}>
                    {phase.items.map((item, i) => (
                      <li key={i} style={{ marginBottom: 'var(--space-xs)', fontSize: '0.875rem' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Why This Approach - AI Generated Section */}
            <div className="proposal-section">
              <h3 className="proposal-section-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <Target size={18} style={{ color: 'var(--accent-primary)' }} />
                Why This Approach
              </h3>
              <div style={{ padding: 'var(--space-lg)', background: 'var(--glass-bg)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--accent-primary)', color: 'var(--text-muted)', textAlign: 'center' }}>
                <Sparkles size={24} style={{ marginBottom: 'var(--space-sm)', color: 'var(--accent-primary)' }} />
                <p>AI-generated content will appear here</p>
                <p style={{ fontSize: '0.75rem', marginTop: 'var(--space-xs)' }}>Click "Generate proposal content" to populate this section</p>
              </div>
            </div>

            {/* Pricing Overview */}
            <div className="proposal-section">
              <h3 className="proposal-section-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <DollarSign size={18} style={{ color: 'var(--accent-primary)' }} />
                Pricing Overview
              </h3>
              <div className="pricing-block">
                <div className="pricing-row">
                  <span style={{ color: 'var(--text-muted)' }}>Labour</span>
                  <span style={{ color: 'var(--text-primary)' }}>£{mockProposal.pricing.labour.toLocaleString()}</span>
                </div>
                <div className="pricing-row">
                  <span style={{ color: 'var(--text-muted)' }}>Materials</span>
                  <span style={{ color: 'var(--text-primary)' }}>£{mockProposal.pricing.materials.toLocaleString()}</span>
                </div>
                <div className="pricing-row">
                  <span style={{ color: 'var(--text-muted)' }}>Fixtures & Fittings</span>
                  <span style={{ color: 'var(--text-primary)' }}>£{mockProposal.pricing.fixtures.toLocaleString()}</span>
                </div>
                <div className="pricing-row">
                  <span style={{ color: 'var(--text-muted)' }}>Contingency (10%)</span>
                  <span style={{ color: 'var(--text-primary)' }}>£{mockProposal.pricing.contingency.toLocaleString()}</span>
                </div>
                <div className="pricing-row" style={{ borderTop: '1px solid var(--glass-border)', marginTop: 'var(--space-md)', paddingTop: 'var(--space-md)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>£{mockProposal.pricing.subtotal.toLocaleString()}</span>
                </div>
                <div className="pricing-row">
                  <span style={{ color: 'var(--text-muted)' }}>VAT (20%)</span>
                  <span style={{ color: 'var(--text-primary)' }}>£{mockProposal.pricing.vat.toLocaleString()}</span>
                </div>
                <div className="pricing-row total">
                  <span>Total Investment</span>
                  <span className="price">£{mockProposal.pricing.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="responsive-grid-2" style={{ gap: 'var(--space-md)', marginTop: 'var(--space-xl)' }}>
              <GlassPanel>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>Estimated Timeline</div>
                <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{mockProposal.timeline}</div>
              </GlassPanel>
              <GlassPanel>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>Warranty</div>
                <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{mockProposal.warranty}</div>
              </GlassPanel>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button className="btn btn-primary" disabled style={{ flex: 1 }}><Send size={16} />Send to Client</button>
          <button className="btn btn-secondary" disabled style={{ flex: 1 }}><Download size={16} />Export PDF</button>
        </div>
      </div>

      {/* AI Side Panel - MANDATORY */}
      <div className="ai-side-panel">
        <AIPanel 
          title="Proposal AI" 
          description="Generate compelling proposal content tailored to your client."
        >
          <div className="ai-actions-grid single-col">
            <AIButton icon={Wand2} variant="full" tooltip="AI generation will be enabled soon">
              Generate proposal content
            </AIButton>
            <AIButton icon={MessageSquare} variant="full" tooltip="AI generation will be enabled soon">
              Rewrite in simpler language
            </AIButton>
            <AIButton icon={Target} variant="full" tooltip="AI generation will be enabled soon">
              Tailor for this client
            </AIButton>
          </div>
        </AIPanel>

        {/* Proposal Sections AI Panel */}
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <GlassPanel title="AI Section Generator" titleIcon={Sparkles}>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
              Generate content for specific sections:
            </p>
            <div className="ai-actions-grid single-col">
              <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
                Introduction
              </AIButton>
              <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
                Scope summary
              </AIButton>
              <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
                Why this approach
              </AIButton>
              <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
                Pricing overview
              </AIButton>
            </div>
          </GlassPanel>
        </div>

        <AIExplanationBox>
          AI creates professional proposal content based on your quote data. All content can be edited before sending.
        </AIExplanationBox>
      </div>
    </div>
  );
}

export default ProposalDetail;
